/**
 * Planning Live Dashboard
 *
 * Works without any Plan of Record, plan cycle, or scenario.
 * Reads directly from the live items and relationship stores to show:
 *   - Active item count and rough capacity demand (from work-size altitude inputs)
 *   - Items blocked or warned by the relationship rule engine
 *   - Size band distribution
 *   - Deferred / archived items
 *
 * This is the "do stuff without a plan" surface.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  ExternalLink,
  GitBranch,
  Layers,
  Loader2,
  Pause,
  Shield,
  TrendingUp,
  Zap,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useItemRelationshipsStore } from '../../stores/prioritization/itemRelationshipsStore';
import {
  buildLiveItemSummaries,
  WORK_SIZE_BAND_LABELS,
  type WorkSizeBand,
} from '../../lib/itemToPlanningAdapter';
import { evaluateItemRelationships } from '../../lib/itemRelationshipRuleEngine';
import {
  listPlanCycles,
  getActivePlanOfRecord,
  listPlanVersions,
} from '../../lib/planningService';
import { porAuthorizable } from '../../lib/planningQueries';
import {
  formatProductShellDateLabel,
  greetingForHour,
  PRODUCT_SHELL_DATE_CLASS,
  PRODUCT_SHELL_SUBTITLE_CLASS,
  PRODUCT_SHELL_TITLE_CLASS,
} from '../../lib/productShellPageStyles';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { usePlanningDefaultsStore } from '../../stores/prioritization/planningDefaultsStore';
import type { PlanCycle } from '../../types/planning';
import { useItemHref } from '@/lib/itemLinkScope';

// Sized buckets only — 'unknown' is data completeness, not a size, so it is
// reported as a footnote below the chart rather than a bar competing on the
// same scale (a large unsized pool would otherwise flatten the real signal).
const BAND_ORDER: WorkSizeBand[] = [
  '1-2_weeks',
  '3-8_weeks',
  '9-26_weeks',
  '27-52_weeks',
  '52+_weeks',
];

type GateHealthTier = 'clean' | 'warn' | 'block';

interface GateHealthEntry {
  itemId: string;
  title: string;
  level: 'warn' | 'block';
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
  accent?: 'default' | 'warn' | 'block' | 'green';
  onClick?: () => void;
}

function StatCard({ icon, label, value, sub, accent = 'default', onClick }: StatCardProps) {
  const accentClass =
    accent === 'warn'
      ? 'border-[var(--mustard)] dark:border-[var(--mustard)]/60'
      : accent === 'block'
      ? 'border-[var(--coral)] dark:border-[var(--coral)]/60'
      : accent === 'green'
      ? 'border-[var(--green)] dark:border-[var(--green)]/60'
      : 'border-[var(--rule)] dark:border-[var(--rule)]';

  const iconClass =
    accent === 'warn'
      ? 'text-[var(--mustard)]'
      : accent === 'block'
      ? 'text-[var(--coral)]'
      : accent === 'green'
      ? 'text-[var(--green)] dark:text-[var(--text-ter)]'
      : 'text-primary-500';

  const interactive = Boolean(onClick);

  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={` border ${accentClass} bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] p-5 flex items-start gap-4${
        interactive ? ' cursor-pointer hover:shadow-sm transition-shadow' : ''
      }`}
    >
      <div className={`mt-0.5 flex-shrink-0 ${iconClass}`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide">
          {label}
        </p>
        <p className="text-2xl font-bold text-[var(--text-pri)] dark:text-[var(--text-pri)] mt-0.5 tabular-nums">
          {value}
        </p>
        {sub && (
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">{sub}</p>
        )}
      </div>
    </div>
  );
}

function GateHealthRow({
  tier,
  label,
  count,
  icon: Icon,
  containerClass,
  textClass,
  countClass,
  iconClass,
  expanded,
  onToggle,
  entries,
}: {
  tier: GateHealthTier;
  label: string;
  count: number;
  icon: typeof CheckCircle2;
  containerClass: string;
  textClass: string;
  countClass: string;
  iconClass: string;
  expanded: boolean;
  onToggle: (tier: GateHealthTier) => void;
  entries: GateHealthEntry[];
}) {
  const itemHref = useItemHref();
  const interactive = tier !== 'clean' || count > 0;

  return (
    <div>
      <button
        type="button"
        onClick={() => interactive && onToggle(tier)}
        disabled={!interactive}
        className={`w-full flex items-center justify-between p-3  border text-left transition-colors ${containerClass} ${
          interactive ? 'hover:brightness-[0.98] cursor-pointer' : 'cursor-default'
        }`}
      >
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${iconClass}`} />
          <span className={`text-sm font-medium ${textClass}`}>{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-lg font-bold tabular-nums ${countClass}`}>{count}</span>
          {interactive && (
            expanded ? (
              <ChevronUp className="h-3.5 w-3.5 opacity-60" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
            )
          )}
        </div>
      </button>
      {expanded && entries.length > 0 && (
        <ul className="mt-2 space-y-1 pl-2">
          {entries.map((entry) => (
            <li key={entry.itemId}>
              <Link
                to={itemHref(entry.itemId, 'relationships')}
                className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors group"
              >
                <GitBranch className="h-3.5 w-3.5 text-[var(--text-ter)] group-hover:text-primary-500 flex-shrink-0" />
                <span className="truncate group-hover:text-primary-700 dark:group-hover:text-primary-300">
                  {entry.title}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {expanded && tier === 'clean' && count > 0 && (
        <p className="mt-2 px-3 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          All {count} active items passed relationship gates at inclusion time.
        </p>
      )}
    </div>
  );
}

// ── Governance Status Widget ───────────────────────────────────────────────────

interface CycleGovernanceState {
  cycle: PlanCycle;
  activePorVersionId: string | null;
  latestVersionId: string | null;
  porReady: boolean | null;
  blockerCount: number;
  versionCount: number;
}

function GovernanceStatusBanner({ states, loading }: { states: CycleGovernanceState[]; loading: boolean }) {
  if (loading) {
    return (
      <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] p-5 flex items-center gap-3 mb-8">
        <Loader2 className="h-4 w-4 animate-spin text-[var(--text-ter)]" />
        <span className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">Loading plan governance status…</span>
      </div>
    );
  }

  if (states.length === 0) return null;

  return (
    <div className="mb-8 space-y-3">
      <div className="flex items-center gap-2 mb-3">
        <Shield className="h-4 w-4 text-primary-600 dark:text-primary-400" />
        <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Plan Governance Status</h2>
        <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          — Doc 108 primary situational awareness surface
        </span>
      </div>

      {states.map(({ cycle, activePorVersionId, latestVersionId, porReady, blockerCount, versionCount }) => {
        const hasPor = Boolean(activePorVersionId);
        const readinessColor = porReady === true
          ? 'text-[var(--green)] dark:text-[var(--text-ter)]'
          : porReady === false
            ? 'text-[var(--mustard)] dark:text-[var(--mustard)]'
            : 'text-[var(--text-ter)] dark:text-[var(--text-sec)]';

        return (
          <div
            key={cycle.id}
            className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] overflow-hidden"
          >
            {/* Cycle header row */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--rule)] dark:border-[var(--rule)]">
              <div className="flex items-center gap-2">
                <span
                  className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                    cycle.status === 'active'
                      ? 'bg-[var(--green)]'
                      : cycle.status === 'provisional'
                        ? 'bg-[var(--mustard)]'
                        : 'bg-[var(--surface)]'
                  }`}
                />
                <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                  {cycle.name}
                </span>
                <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--hover-tint)] dark:bg-[var(--bg)] text-[var(--text-ter)] dark:text-[var(--text-sec)] capitalize">
                  {cycle.status}
                </span>
              </div>
              <Link
                to={`/planning/plans/${cycle.id}`}
                className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
              >
                Open cycle
                <ExternalLink className="h-3 w-3" />
              </Link>
            </div>

            {/* Governance widgets row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-[var(--rule)] dark:divide-[var(--rule)]">
              {/* POR status */}
              <div className="px-4 py-3">
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-1">
                  Plan of Record
                </p>
                {hasPor ? (
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 text-[var(--green)] dark:text-[var(--text-ter)]" />
                    <span className="text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)]">Active POR</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                    <span className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">None authorized</span>
                  </div>
                )}
              </div>

              {/* Authorization readiness */}
              <div className="px-4 py-3">
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-1">
                  POR Readiness
                </p>
                {latestVersionId ? (
                  <span className={`text-sm font-medium ${readinessColor}`}>
                    {porReady === true
                      ? 'Ready to authorize'
                      : porReady === false
                        ? `${blockerCount} blocker${blockerCount !== 1 ? 's' : ''}`
                        : '—'}
                  </span>
                ) : (
                  <span className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">No versions</span>
                )}
              </div>

              {/* Plan versions */}
              <div className="px-4 py-3">
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-1">
                  Plan Versions
                </p>
                <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                  {versionCount}
                </span>
              </div>

              {/* Quick action */}
              <div className="px-4 py-3 flex items-center">
                <Link
                  to={`/planning/plans/${cycle.id}?tab=governance`}
                  className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                >
                  Governance view
                  <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function PlanningLiveDashboard() {
  const navigate = useNavigate();
  const now = new Date();
  const userName = useUserStore((s) => s.userName);
  const currentWorkspace = useTenantStore((s) => s.getCurrentWorkspace?.()) ?? null;

  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const edges = useItemRelationshipsStore((s) => s.edges);
  const groups = useItemRelationshipsStore((s) => s.groups);
  const memberships = useItemRelationshipsStore((s) => s.groupMemberships);
  const fetchRelationships = useItemRelationshipsStore((s) => s.fetchFromSupabase);

  const [loading, setLoading] = useState(items.length === 0);
  const [expandedGateTier, setExpandedGateTier] = useState<GateHealthTier | null>(null);

  // Doc 108: governance status panel
  const [govLoading, setGovLoading] = useState(true);
  const [govStates, setGovStates] = useState<CycleGovernanceState[]>([]);

  const openEngineWithLiveRun = () => {
    navigate('/planning/engine?run=live', { state: { autoRunLive: true } });
  };

  const toggleGateTier = (tier: GateHealthTier) => {
    setExpandedGateTier((current) => (current === tier ? null : tier));
  };

  useEffect(() => {
    let mounted = true;
    Promise.all([fetchItems(), fetchRelationships()]).finally(() => {
      if (mounted) setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetchItems, fetchRelationships]);

  // Load governance status for active plan cycles (Doc 108 §3 — primary situational awareness)
  useEffect(() => {
    let mounted = true;
    async function loadGovernanceStatus() {
      const { data: cycles } = await listPlanCycles({ status: 'active' });
      if (!mounted) return;
      if (cycles.length === 0) {
        setGovStates([]);
        setGovLoading(false);
        return;
      }

      const stateResults = await Promise.all(
        cycles.map(async (cycle) => {
          const [porRes, versRes] = await Promise.all([
            getActivePlanOfRecord(cycle.id),
            listPlanVersions(cycle.id),
          ]);
          const versions = versRes.data ?? [];
          const latestVersion = versions[0] ?? null;

          let porReady: boolean | null = null;
          let blockerCount = 0;
          if (latestVersion) {
            const authResult = await porAuthorizable(latestVersion.id, {
              relaxBucketsAndEvaluationForLegacy: true,
            });
            porReady = authResult.ready;
            blockerCount = authResult.blockers.length;
          }

          return {
            cycle,
            activePorVersionId: porRes.data?.plan_version_id ?? null,
            latestVersionId: latestVersion?.id ?? null,
            porReady,
            blockerCount,
            versionCount: versions.length,
          } satisfies CycleGovernanceState;
        }),
      );

      if (mounted) {
        setGovStates(stateResults);
        setGovLoading(false);
      }
    }
    void loadGovernanceStatus();
    return () => {
      mounted = false;
    };
  }, []);

  const activeItems = useMemo(
    () => items.filter((i) => !i.exit_status || i.exit_status === 'active'),
    [items],
  );
  const deferredItems = useMemo(
    () => items.filter((i) => i.exit_status === 'deferred'),
    [items],
  );

  const itemEffortBandHours = usePlanningDefaultsStore((s) => s.defaults.itemEffortBandHours);
  const summaries = useMemo(
    () => buildLiveItemSummaries(activeItems, itemEffortBandHours),
    [activeItems, itemEffortBandHours],
  );

  const totalEstimatedWUs = useMemo(
    () => summaries.reduce((s, r) => s + r.estimatedWUs, 0),
    [summaries],
  );

  const bandCounts = useMemo(() => {
    const counts: Record<WorkSizeBand, number> = {
      '1-2_weeks': 0,
      '3-8_weeks': 0,
      '9-26_weeks': 0,
      '27-52_weeks': 0,
      '52+_weeks': 0,
      unknown: 0,
    };
    for (const s of summaries) counts[s.workSizeBand]++;
    return counts;
  }, [summaries]);

  // Gate health: run rule engine for every active item, tally warns/blocks
  const gateResults = useMemo((): GateHealthEntry[] => {
    const results: GateHealthEntry[] = [];
    for (const item of activeItems) {
      const frames = item.frames ?? [];
      const ctx = {
        itemId: item.id,
        allGroups: groups,
        allMemberships: memberships,
        allEdges: edges,
        itemFrames: frames,
        scopeItemIds: null,
        activeFrameId: null,
        declaredTrackRole: null as null,
        selectedElaborationChildId: null,
        ringAtTime: 3 as const,
        isCommitmentTime: false,
      };
      const result = evaluateItemRelationships(ctx);
      if (result.summary === 'block' || result.summary === 'require_approval') {
        results.push({ itemId: item.id, title: item.title || 'Untitled item', level: 'block' });
      } else if (result.summary === 'warn') {
        results.push({ itemId: item.id, title: item.title || 'Untitled item', level: 'warn' });
      }
    }
    return results;
  }, [activeItems, groups, memberships, edges]);

  const blockedEntries = useMemo(
    () => gateResults.filter((r) => r.level === 'block'),
    [gateResults],
  );
  const warnEntries = useMemo(
    () => gateResults.filter((r) => r.level === 'warn'),
    [gateResults],
  );
  const blockedCount = blockedEntries.length;
  const warnCount = warnEntries.length;
  const cleanCount = activeItems.length - blockedCount - warnCount;

  const scrollToGateHealth = () => {
    document.getElementById('planning-gate-health')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (blockedCount > 0) {
      setExpandedGateTier('block');
    } else if (warnCount > 0) {
      setExpandedGateTier('warn');
    }
  };

  const maxBandCount = Math.max(...BAND_ORDER.map((b) => bandCounts[b]), 1);
  const sizedCount = BAND_ORDER.reduce((sum, b) => sum + bandCounts[b], 0);
  const unsizedCount = bandCounts['unknown'] ?? 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      {/* Header */}
      <div className="mb-8">
        <p className={PRODUCT_SHELL_DATE_CLASS}>{formatProductShellDateLabel(now)}</p>
        <h1 className={PRODUCT_SHELL_TITLE_CLASS}>
          {greetingForHour(now)}
          {userName ? `, ${userName.split(' ')[0]}` : ''}.
        </h1>
        <p className={PRODUCT_SHELL_SUBTITLE_CLASS}>
          {currentWorkspace?.name || 'Your workspace'} — live view, no plan required
        </p>
      </div>

      {/* Doc 108 Governance Status Banner — shown when active plan cycles exist */}
      <GovernanceStatusBanner states={govStates} loading={govLoading} />

      {/* Top stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={<Activity className="h-5 w-5" />}
          label="Active items"
          value={activeItems.length}
          sub="in active consideration"
          accent="green"
        />
        <StatCard
          icon={<TrendingUp className="h-5 w-5" />}
          label="Estimated demand"
          value={`~${totalEstimatedWUs} WU`}
          sub="from altitude size inputs"
        />
        <StatCard
          icon={<AlertTriangle className="h-5 w-5" />}
          label="Gate warnings"
          value={warnCount}
          sub={
            blockedCount > 0 || warnCount > 0
              ? `${blockedCount} blocked — click to review`
              : `${blockedCount} blocked`
          }
          accent={blockedCount > 0 ? 'block' : warnCount > 0 ? 'warn' : 'default'}
          onClick={blockedCount > 0 || warnCount > 0 ? scrollToGateHealth : undefined}
        />
        <StatCard
          icon={<Pause className="h-5 w-5" />}
          label="Deferred"
          value={deferredItems.length}
          sub="not in active consideration"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Size band distribution */}
        <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] p-5">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="h-4 w-4 text-primary-500" />
            <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              Work size distribution
            </h2>
          </div>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-4">
            From altitude scoping inputs — {sizedCount} sized item{sizedCount === 1 ? '' : 's'}.
          </p>
          <div className="space-y-2.5">
            {BAND_ORDER.map((band) => {
              const count = bandCounts[band];
              const pct = Math.round((count / maxBandCount) * 100);
              return (
                <div key={band} className="flex items-center gap-3">
                  <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] w-16 flex-shrink-0 text-right">
                    {WORK_SIZE_BAND_LABELS[band]}
                  </span>
                  <div className="flex-1 h-5 bg-[var(--hover-tint)] dark:bg-[var(--bg)] rounded overflow-hidden">
                    <div
                      className="h-full bg-primary-500 dark:bg-primary-400 rounded transition-all duration-300"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium tabular-nums text-[var(--text-sec)] dark:text-[var(--text-pri)] w-6 text-right">
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
          {unsizedCount > 0 && (
            <p className="mt-4 pt-3 border-t border-[var(--rule)] dark:border-[var(--rule)] text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              {unsizedCount} item{unsizedCount === 1 ? '' : 's'} awaiting size inputs — not shown
              above.
            </p>
          )}
        </div>

        {/* Gate health */}
        <div
          id="planning-gate-health"
          className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] p-5"
        >
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <GitBranch className="h-4 w-4 text-primary-500" />
              <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                Relationship gate health
              </h2>
            </div>
            <Link
              to={
                blockedCount > 0
                  ? '/prioritization/reports/relationship-health?level=block'
                  : warnCount > 0
                    ? '/prioritization/reports/relationship-health?level=warn'
                    : '/prioritization/reports/relationship-health'
              }
              className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
            >
              Full health report
              <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-4">
            Rule engine evaluated at inclusion time (not commitment). Active items only. Click a row to
            see affected items.
          </p>
          <div className="space-y-3">
            <GateHealthRow
              tier="clean"
              label="Clean"
              count={cleanCount}
              icon={CheckCircle2}
              containerClass="bg-[var(--green-dim)] dark:bg-[var(--green)]/20 border-[var(--green)] dark:border-[var(--green)]/40"
              textClass="text-[var(--green)] dark:text-[var(--text-ter)]"
              countClass="text-[var(--green)] dark:text-[var(--text-ter)]"
              iconClass="text-[var(--green)] dark:text-[var(--text-ter)]"
              expanded={expandedGateTier === 'clean'}
              onToggle={toggleGateTier}
              entries={[]}
            />
            <GateHealthRow
              tier="warn"
              label="Warn"
              count={warnCount}
              icon={AlertTriangle}
              containerClass="bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border-[var(--mustard)] dark:border-[var(--mustard)]/40"
              textClass="text-[var(--mustard)] dark:text-[var(--mustard)]"
              countClass="text-[var(--mustard)] dark:text-[var(--mustard)]"
              iconClass="text-[var(--mustard)] dark:text-[var(--mustard)]"
              expanded={expandedGateTier === 'warn'}
              onToggle={toggleGateTier}
              entries={warnEntries}
            />
            <GateHealthRow
              tier="block"
              label="Blocked / needs approval"
              count={blockedCount}
              icon={AlertTriangle}
              containerClass="bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border-[var(--coral)] dark:border-[var(--coral)]/40"
              textClass="text-[var(--coral)] dark:text-[var(--coral)]"
              countClass="text-[var(--coral)] dark:text-[var(--coral)]"
              iconClass="text-[var(--coral)] dark:text-[var(--coral)]"
              expanded={expandedGateTier === 'block'}
              onToggle={toggleGateTier}
              entries={blockedEntries}
            />
          </div>
        </div>
      </div>

      {/* Planning Engine CTA — with live items */}
      <div className="border border-primary-200 dark:border-primary-800/60 bg-primary-50 dark:bg-primary-900/20 p-5 flex items-start gap-4">
        <Zap className="h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-primary-900 dark:text-primary-200">
            Planning Engine (live items mode)
          </p>
          <p className="text-xs text-primary-700 dark:text-primary-400 mt-0.5">
            Run a capacity scenario using your {activeItems.length} active items as input — no manual data entry.
            Work size from altitude inputs maps to effort estimates automatically.
          </p>
        </div>
        <button
          type="button"
          onClick={openEngineWithLiveRun}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 dark:bg-primary-500 text-white text-xs font-medium hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
        >
          <Layers className="h-3.5 w-3.5" />
          Run on live items
        </button>
      </div>

      {/* Planning Journey Checklist */}
      <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] overflow-hidden">
        <div className="px-5 py-3 border-b border-[var(--rule)] dark:border-[var(--rule)] flex items-center justify-between">
          <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Planning Journey</span>
          <span className="text-xs text-[var(--text-ter)]">9 steps · Setup → Engine → Plans → Governance</span>
        </div>
        <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
          {([
            { step: 1, label: 'Set up team', sub: 'People & Capacity', href: '/planning/people-capacity', system: 'setup' },
            { step: 2, label: 'Create a scenario', sub: 'Engine → Scenarios', href: '/planning/engine/scenarios', system: 'engine' },
            { step: 3, label: 'Sync priorities', sub: 'Engine → Priorities', href: '/planning/engine/priorities', system: 'engine' },
            { step: 4, label: 'Add work units', sub: 'Engine → Work Units', href: '/planning/engine/work-units', system: 'engine' },
            { step: 5, label: 'Set resource budget', sub: 'Engine → Resource Budget', href: '/planning/engine/capacity', system: 'engine' },
            { step: 6, label: 'Run the engine', sub: 'Engine → Overview', href: '/planning/engine', system: 'engine' },
            { step: 7, label: 'Review engine output', sub: 'Engine → Engine Output / Above-Below Line', href: '/planning/engine/above-below', system: 'engine' },
            { step: 8, label: 'Authorize the Plan of Record', sub: 'Plans → Plan detail', href: '/planning/plans', system: 'plans' },
            { step: 9, label: 'Govern', sub: 'Plans → Governance', href: '/planning/plans', system: 'plans' },
          ] as const).map(({ step, label, sub, href, system }) => {
            const color = system === 'engine' ? 'text-[var(--text-ter)] dark:text-[var(--text-ter)]' : system === 'plans' ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]';
            return (
              <Link key={step} to={href} className="flex items-center gap-3 px-5 py-2.5 hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors group">
                <span className="w-5 h-5 rounded-full border border-[var(--rule)] dark:border-[var(--rule)] flex items-center justify-center text-xs text-[var(--text-ter)] shrink-0 group-hover:border-primary-400">
                  {step}
                </span>
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">{label}</span>
                  <span className={`text-xs ml-2 ${color}`}>{sub}</span>
                </div>
                <ExternalLink className="h-3.5 w-3.5 text-[var(--text-ter)] group-hover:text-[var(--text-ter)] shrink-0" />
              </Link>
            );
          })}
        </div>
      </div>

      {activeItems.length === 0 && (
        <div className="mt-6 border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] p-8 text-center">
          <Clock className="h-8 w-8 text-[var(--text-ter)] dark:text-[var(--text-ter)] mx-auto mb-3" />
          <p className="text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">No active items yet</p>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">
            Items you add in Prioritization appear here automatically.
          </p>
          <Link
            to="/prioritization/items"
            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            Go to Items
          </Link>
        </div>
      )}
    </div>
  );
}
